import { useMemo, useState } from 'react';
import { ArrowLeft, CircleStop, Pause, Play, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn } from '@Utils';

import { Badge, ConfirmationModal } from '@Components';
import Modal from '@Components/ui/modal';
import Spinner from '@Components/ui/spinner';
import { Button } from '@Components/ui-v2/button';
import { Empty, EmptyHeader, EmptyTitle } from '@Components/ui-v2/empty';
import { Input } from '@Components/ui-v2/input';
import {
  storeKey,
  useActiveStoreNames,
  useHasPermission,
  useInvalidateScheduleLists,
  useScheduleStoreAssignments,
  useScheduleTasks,
  useScheduleWeeks,
} from '@CustomHooks';
import { Permission, ScheduleDetail as ScheduleDetailModel, TaskType } from '@Models';
import { bopsApi } from '@Network';
import {
  getRetailerColor,
  getRetailerName,
  getRetailerShort,
  UPPERCASE_LABEL_CLASS_NAME,
} from '../../FieldServices/constants';
import DeleteScheduleDialog from '../components/deleteScheduleDialog';
import getScheduleStatusVariant, { getConflictMessage } from '../constants';
import {
  ALERT_CEILING_HYBRID_MAX,
  ALERT_CEILING_HYBRID_MIN,
  buildCadenceDiff,
  buildFrequencyStateFromSchedule,
  formatScheduleRecurrence,
  getWorkMixOption,
  isFrequencyStateValid,
  WORK_MIX_OPTIONS,
} from '../Wizard/constants';
import { ScheduleWizardFrequencyState } from '../Wizard/scheduleWizard.types';
import Step3Frequency from '../Wizard/steps/step3Frequency';
import {
  buildCadenceCalendar,
  CADENCE_CALENDAR_WEEKS,
  getCadenceWindowStart,
  getScheduleComposition,
  getScheduleVisitDates,
  getScheduleWeeksRange,
} from './constants';
import ScheduleNameEditor from './scheduleNameEditor';

interface ScheduleConfigurationProps {
  detail: ScheduleDetailModel;
  taskTypes: TaskType[];
  onScheduleChanged: () => Promise<void>;
}

const CARD = 'rounded-xl border border-gray-200 bg-white';
const SECTION_LABEL = UPPERCASE_LABEL_CLASS_NAME;

const ScheduleConfiguration = ({ detail, taskTypes, onScheduleChanged }: ScheduleConfigurationProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canManage = useHasPermission(Permission.ManageSchedules);
  const { schedule, stores_count: storesCount, retailers, firms } = detail;

  const [isStoresOpen, setIsStoresOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [draftCeiling, setDraftCeiling] = useState<number | null>(null);
  const [isSavingCeiling, setIsSavingCeiling] = useState(false);
  const [pendingCeiling, setPendingCeiling] = useState<number | null>(null);
  const [draftFrequency, setDraftFrequency] = useState<ScheduleWizardFrequencyState | null>(null);
  const [isSavingFrequency, setIsSavingFrequency] = useState(false);
  const [ceilingError, setCeilingError] = useState<string | null>(null);
  const [frequencyError, setFrequencyError] = useState<string | null>(null);
  const [storeSearch, setStoreSearch] = useState('');

  const retailer = retailers[0] ?? null;
  const composition = getScheduleComposition(schedule.alert_ceiling_pct);
  const recurrence = formatScheduleRecurrence(schedule, t, i18n.language);

  const today = new Date();
  const weeksRange = getScheduleWeeksRange(today);
  const weeksQuery = useScheduleWeeks({
    scheduleId: schedule.id,
    from: weeksRange.from,
    to: weeksRange.to,
    enabled: composition !== 'alerts_only',
  });

  const calendar = useMemo(() => {
    const weeks = weeksQuery.data ?? [];
    const windowStart = getCadenceWindowStart(weeks, weeksRange.from);
    const visitDates = getScheduleVisitDates(schedule, weeks, windowStart, weeksRange.to);
    const windowEnd = buildCadenceCalendar(windowStart, [], weeksRange.from).slice(-1)[0]?.date ?? windowStart;
    return {
      windowStart,
      cells: buildCadenceCalendar(windowStart, visitDates, weeksRange.from),
      upcoming: visitDates.filter((date) => date >= weeksRange.from),
      inWindow: visitDates.filter((date) => date >= weeksRange.from && date <= windowEnd),
    };
  }, [schedule, weeksQuery.data, weeksRange.from, weeksRange.to]);

  const nextVisit = calendar.upcoming[0] ?? null;
  const plannedVisits = calendar.inWindow.length * storesCount;

  const defaultListQuery = useScheduleTasks({ scheduleId: schedule.id, weekStart: null });
  const scheduleTaskTypes = useMemo(() => {
    const names = new Map(taskTypes.map((taskType) => [taskType.id, taskType.name]));
    const categoriesById = new Map<string, Set<string>>();
    (defaultListQuery.data?.items ?? []).forEach((task) => {
      const categories = categoriesById.get(task.task_type_id) ?? new Set<string>();
      task.categories.forEach((category) => categories.add(category));
      categoriesById.set(task.task_type_id, categories);
    });
    return Array.from(categoriesById.entries()).flatMap(([id, categories]) => {
      const name = names.get(id);
      return name === undefined ? [] : [{ id, name, categories: Array.from(categories).sort() }];
    });
  }, [defaultListQuery.data, taskTypes]);

  const visitTarget = useMemo(() => {
    const targets = firms.map((firm) => firm.visit_task_target_max).filter((max): max is number => max !== null);
    return targets.length > 0 ? Math.max(...targets) : null;
  }, [firms]);

  const assignmentsQuery = useScheduleStoreAssignments(schedule.id, isStoresOpen);
  const storeNamesQuery = useActiveStoreNames(retailers.length === 1 ? retailer : null, isStoresOpen);

  const storeRows = useMemo(() => {
    const names = storeNamesQuery.data ?? {};
    return (assignmentsQuery.data ?? []).map((assignment) => {
      const key = storeKey(assignment.retailer, assignment.location_code);
      return { key, locationCode: assignment.location_code, name: names[key] ?? null, isActive: key in names };
    });
  }, [assignmentsQuery.data, storeNamesQuery.data]);

  const visibleStoreRows = useMemo(() => {
    const search = storeSearch.trim().toLowerCase();
    if (search === '') return storeRows;
    return storeRows.filter((row) => `${row.name ?? ''} ${row.locationCode}`.toLowerCase().includes(search));
  }, [storeRows, storeSearch]);

  const isStoresPending = assignmentsQuery.isPending || storeNamesQuery.isPending;
  const isStoresError = assignmentsQuery.isError || storeNamesQuery.isError;

  const invalidateLists = useInvalidateScheduleLists(schedule.id);
  const isPaused = schedule.status === 'paused';

  const refreshAfterWrite = async () => {
    try {
      await onScheduleChanged();
      await invalidateLists();
    } catch {
      showErrorToast(t('Schedule.Detail.ListRefreshError'));
    }
  };

  const handleToggleStatus = async () => {
    const nextStatus = isPaused ? 'active' : 'paused';
    setIsUpdatingStatus(true);
    try {
      await bopsApi.updateScheduleStatus(schedule.id, nextStatus);
    } catch (error) {
      showErrorToast(getConflictMessage(error, t, i18n, 'Schedule.Configuration.StatusError'));
      setIsUpdatingStatus(false);
      return;
    }
    await refreshAfterWrite();
    setIsUpdatingStatus(false);
    showSuccessToast(
      t(isPaused ? 'Schedule.Configuration.ResumedToast' : 'Schedule.Configuration.PausedToast', {
        name: schedule.name,
      }),
    );
  };

  const handleEnd = async () => {
    setIsEndOpen(false);
    setIsUpdatingStatus(true);
    try {
      await bopsApi.updateScheduleStatus(schedule.id, 'ended');
    } catch (error) {
      showErrorToast(getConflictMessage(error, t, i18n, 'Schedule.Configuration.StatusError'));
      setIsUpdatingStatus(false);
      return;
    }
    await refreshAfterWrite();
    setIsUpdatingStatus(false);
    showSuccessToast(t('Schedule.Configuration.EndedToast', { name: schedule.name }));
  };

  const hasStarted = detail.has_started_visit_week;
  const canEditCeiling = canManage && schedule.status !== 'ended';

  const saveCeiling = async (value: number) => {
    setIsSavingCeiling(true);
    try {
      await bopsApi.updateSchedule(schedule.id, { alert_ceiling_pct: value });
    } catch (error) {
      setCeilingError(getConflictMessage(error, t, i18n, 'Schedule.Configuration.Ceiling.SaveError'));
      setIsSavingCeiling(false);
      setPendingCeiling(null);
      return;
    }
    await refreshAfterWrite();
    setDraftCeiling(null);
    setIsSavingCeiling(false);
    setPendingCeiling(null);
  };

  const handleCeilingSave = () => {
    if (draftCeiling === null) return;
    const switchesComposition = getWorkMixOption(draftCeiling) !== getWorkMixOption(schedule.alert_ceiling_pct);
    if (switchesComposition) {
      setPendingCeiling(draftCeiling);
      return;
    }
    saveCeiling(draftCeiling).catch(() => {});
  };

  const editableFrequency = buildFrequencyStateFromSchedule(schedule);

  const saveFrequency = async () => {
    if (draftFrequency === null) return;
    const diff = buildCadenceDiff(schedule, draftFrequency);
    if (Object.keys(diff).length === 0) {
      setDraftFrequency(null);
      return;
    }
    setIsSavingFrequency(true);
    try {
      await bopsApi.updateSchedule(schedule.id, diff);
    } catch (error) {
      setFrequencyError(getConflictMessage(error, t, i18n, 'Schedule.Configuration.Cadence.SaveError'));
      setIsSavingFrequency(false);
      return;
    }
    await refreshAfterWrite();
    setDraftFrequency(null);
    setIsSavingFrequency(false);
    showSuccessToast(t('Schedule.Configuration.Cadence.Saved'));
  };

  const formatDate = (date: string): string =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString(i18n.language, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });

  const tiles = [
    { label: t('Schedule.Configuration.Tiles.Stores'), value: String(storesCount) },
    { label: t('Schedule.Configuration.Tiles.TaskTypes'), value: String(scheduleTaskTypes.length) },
    { label: t('Schedule.Configuration.Tiles.Cadence'), value: recurrence },
    { label: t('Schedule.Configuration.Tiles.FieldServices'), value: String(firms.length) },
  ];

  return (
    <div className="flex h-full flex-col bg-gray-50">
      <div className="flex items-start gap-4 border-b border-gray-200 bg-white px-7 py-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/schedules/${schedule.id}`)}
          className="h-8 flex-shrink-0 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
          <ArrowLeft size={16} />
          {t('Schedule.Configuration.BackToSchedule')}
        </Button>
        <div className="min-w-0">
          <div className="flex h-8 items-center gap-2">
            <span className={SECTION_LABEL}>{t('Schedule.Configuration.Eyebrow')}</span>
            <Badge
              variant={getScheduleStatusVariant(schedule.status)}
              label={t(`Schedule.Status.${schedule.status}`)}
              showDot
              className="rounded-full"
            />
            {schedule.source === 'excel' && (
              <Badge variant="gray" label={t('Schedule.Configuration.ExcelSource')} className="rounded-full" />
            )}
          </div>
          <h1 className="mt-1 truncate text-xl font-semibold tracking-tight text-gray-900">{schedule.name}</h1>
          {retailer !== null && <div className="mt-0.5 text-[13px] text-gray-500">{getRetailerName(retailer)}</div>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-7 py-6">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          <div className={CARD}>
            <div className={cn('border-b border-gray-100 px-5 py-3', SECTION_LABEL)}>
              {t('Schedule.Configuration.Settings')}
            </div>
            <div className="flex flex-col divide-y divide-gray-100">
              <div className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900">
                    {t('Schedule.Configuration.Name.Label')}
                  </div>
                  <div className="mt-0.5 text-[12px] text-gray-500">{t('Schedule.Configuration.Name.Help')}</div>
                </div>
                <div className="flex-shrink-0">
                  <ScheduleNameEditor
                    scheduleId={schedule.id}
                    name={schedule.name}
                    canManage={canManage}
                    onRenamed={onScheduleChanged}
                  />
                </div>
              </div>

              <div className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-gray-900">
                      {t('Schedule.Configuration.Cadence.Label')}
                    </div>
                    <div className="mt-0.5 text-[12px] text-gray-500">{recurrence}</div>
                    <div className="text-[12px] text-gray-500">
                      {t('Schedule.Configuration.Cadence.Dates', {
                        start: schedule.start_date,
                        end: schedule.end_date ?? t('Schedule.Configuration.Cadence.NoEnd'),
                      })}
                    </div>
                  </div>
                  {draftFrequency === null ? (
                    <div className="flex-shrink-0">
                      {editableFrequency === null ? (
                        <span className="text-[12px] text-gray-500">
                          {t('Schedule.Configuration.Cadence.NotEditableLegacy')}
                        </span>
                      ) : (
                        canEditCeiling && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFrequencyError(null);
                              setDraftFrequency(editableFrequency);
                            }}
                            className="h-8">
                            {t('Schedule.Configuration.Cadence.Edit')}
                          </Button>
                        )
                      )}
                    </div>
                  ) : null}
                </div>

                {draftFrequency !== null && (
                  <div className="mt-4 flex flex-col gap-3">
                    <Step3Frequency
                      value={draftFrequency}
                      onChange={(next) => {
                        setFrequencyError(null);
                        setDraftFrequency(next);
                      }}
                      hideHeader
                    />
                    {frequencyError !== null && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                        {frequencyError}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isSavingFrequency}
                        onClick={() => {
                          setFrequencyError(null);
                          setDraftFrequency(null);
                        }}
                        className="h-8">
                        {t('Schedule.Configuration.Ceiling.Cancel')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={
                          isSavingFrequency ||
                          !isFrequencyStateValid(draftFrequency) ||
                          Object.keys(buildCadenceDiff(schedule, draftFrequency)).length === 0
                        }
                        onClick={() => {
                          saveFrequency().catch(() => {});
                        }}
                        className="h-8">
                        {t('Schedule.Configuration.Ceiling.Save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-gray-900">
                      {t('Schedule.Configuration.Ceiling.Label')}
                    </div>
                    <div className="mt-0.5 text-[12px] text-gray-500">
                      {t(`Schedule.Configuration.CeilingNote.${composition}`)}
                    </div>
                  </div>
                  {draftCeiling === null ? (
                    <div className="flex flex-shrink-0 items-center gap-3">
                      <span className="text-[13px] font-semibold text-gray-900">
                        {composition === 'hybrid'
                          ? `${schedule.alert_ceiling_pct}%`
                          : t('Schedule.Configuration.Ceiling.NotApplicable')}
                      </span>
                      {canEditCeiling && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCeilingError(null);
                            setDraftCeiling(schedule.alert_ceiling_pct);
                          }}
                          className="h-8">
                          {t('Schedule.Configuration.Ceiling.Edit')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    getWorkMixOption(draftCeiling) === 'both' && (
                      <span className="flex-shrink-0 text-[13px] font-semibold text-gray-900">{draftCeiling}%</span>
                    )
                  )}
                </div>

                {draftCeiling !== null && (
                  <div className="mt-4 flex flex-col gap-3">
                    <div className="grid grid-cols-3 gap-2">
                      {WORK_MIX_OPTIONS.map((option) => {
                        const optionPct =
                          option.value === 'alertsOnly' ? 100 : option.value === 'scheduleOnly' ? 0 : 50;
                        const isSelected = getWorkMixOption(draftCeiling) === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setCeilingError(null);
                              setDraftCeiling(optionPct);
                            }}
                            className={cn(
                              'flex flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left',
                              isSelected ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white',
                            )}>
                            <option.icon size={16} className="text-gray-500" />
                            <span className="text-[12px] font-semibold text-gray-900">{t(option.labelKey)}</span>
                          </button>
                        );
                      })}
                    </div>

                    {hasStarted && (
                      <span className="text-[12px] text-gray-500">
                        {t('Schedule.Configuration.Ceiling.CurrentWeekKeptHint')}
                      </span>
                    )}

                    {getWorkMixOption(draftCeiling) === 'both' && (
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={ALERT_CEILING_HYBRID_MIN}
                          max={ALERT_CEILING_HYBRID_MAX}
                          step={5}
                          value={draftCeiling}
                          disabled={isSavingCeiling}
                          onChange={(event) => {
                            setCeilingError(null);
                            setDraftCeiling(Number(event.target.value));
                          }}
                          aria-label={t('Schedule.Configuration.Ceiling.Label')}
                          className="flex-1"
                        />
                        <span className="w-12 text-right text-[13px] font-semibold text-gray-900">{draftCeiling}%</span>
                      </div>
                    )}

                    {ceilingError !== null && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                        {ceilingError}
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isSavingCeiling}
                        onClick={() => {
                          setCeilingError(null);
                          setDraftCeiling(null);
                        }}
                        className="h-8">
                        {t('Schedule.Configuration.Ceiling.Cancel')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={isSavingCeiling || draftCeiling === schedule.alert_ceiling_pct}
                        onClick={handleCeilingSave}
                        className="h-8">
                        {t('Schedule.Configuration.Ceiling.Save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900">
                    {t('Schedule.Configuration.VisitTarget.Label')}
                  </div>
                  <div className="mt-0.5 text-[12px] text-gray-500">{t('Schedule.Configuration.VisitTarget.Help')}</div>
                </div>
                <div className="flex-shrink-0 text-[13px] font-semibold text-gray-900">
                  {visitTarget === null
                    ? t('Schedule.Configuration.VisitTarget.NotSet')
                    : t('Schedule.Configuration.VisitTarget.Value', { tasks: String(visitTarget) })}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            {tiles.map((tile) => (
              <div key={tile.label} className={cn(CARD, 'px-4 py-3')}>
                <div className={SECTION_LABEL}>{tile.label}</div>
                <div className="mt-1 truncate text-[15px] font-semibold text-gray-900">{tile.value}</div>
              </div>
            ))}
          </div>

          {composition !== 'alerts_only' && (
            <>
              <div className={cn(CARD, 'border-brand-200 bg-brand-50 px-5 py-4')}>
                <div className={SECTION_LABEL}>{t('Schedule.Configuration.Upcoming.Title')}</div>
                {weeksQuery.isPending ? (
                  <Spinner className="my-4" />
                ) : weeksQuery.isError ? (
                  <div className="mt-2 text-[12px] text-red-600">{t('Schedule.Configuration.Cadence.LoadError')}</div>
                ) : (
                  <>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-brand-700">{plannedVisits}</span>
                      <span className="text-[13px] text-gray-600">
                        {t('Schedule.Configuration.Upcoming.PlannedVisits', { weeks: String(CADENCE_CALENDAR_WEEKS) })}
                      </span>
                    </div>
                    <div className="mt-1 text-[12px] text-gray-500">
                      {nextVisit === null
                        ? t('Schedule.Configuration.Upcoming.None')
                        : t('Schedule.Configuration.Upcoming.Next', { date: formatDate(nextVisit) })}
                    </div>
                  </>
                )}
              </div>

              <div className={cn(CARD, 'px-5 py-4')}>
                <div className="flex items-center justify-between">
                  <span className={SECTION_LABEL}>{t('Schedule.Configuration.Cadence.Label')}</span>
                  <span className="text-[12px] font-semibold text-brand-700">{recurrence}</span>
                </div>
                {weeksQuery.isError ? (
                  <div className="mt-2 text-[12px] text-red-600">{t('Schedule.Configuration.Cadence.LoadError')}</div>
                ) : weeksQuery.isPending ? (
                  <Spinner className="my-8" />
                ) : (
                  <>
                    <div className="mt-3 grid grid-cols-7 gap-1.5">
                      {calendar.cells.slice(0, 7).map((cell) => (
                        <div key={`head-${cell.date}`} className="text-center text-[10.5px] uppercase text-gray-400">
                          {new Date(`${cell.date}T00:00:00Z`).toLocaleDateString(i18n.language, {
                            weekday: 'narrow',
                            timeZone: 'UTC',
                          })}
                        </div>
                      ))}
                      {calendar.cells.map((cell) => (
                        <div
                          key={cell.date}
                          className={cn(
                            'flex h-12 items-center justify-center rounded-lg text-[13px]',
                            cell.isVisit ? 'bg-brand-500 font-semibold text-white' : 'bg-gray-50 text-gray-500',
                            cell.isToday && 'ring-2 ring-brand-500 ring-offset-1',
                          )}>
                          {cell.dayOfMonth}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[11.5px] text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />
                        {t('Schedule.Configuration.Cadence.LegendVisit')}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full border border-brand-500" />
                        {t('Schedule.Configuration.Cadence.LegendToday')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          <div>
            <div className={SECTION_LABEL}>
              {t('Schedule.Configuration.Stores.Title', { stores: String(storesCount) })}
            </div>
            <div className={cn(CARD, 'mt-2 flex items-center gap-3 px-5 py-4')}>
              {retailer !== null && (
                <>
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: getRetailerColor(retailer) }}>
                    {getRetailerShort(retailer)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-gray-900">{getRetailerName(retailer)}</div>
                    <div className="text-[12px] text-gray-500">
                      {t('Schedule.Configuration.Stores.Count', { stores: String(storesCount) })}
                    </div>
                  </div>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => setIsStoresOpen(true)} className="h-8 flex-shrink-0">
                {t('Schedule.Configuration.Stores.ViewMore')}
              </Button>
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>{t('Schedule.Configuration.TaskTypes.Title')}</div>
            {defaultListQuery.isLoading ? (
              <Spinner className="my-4" />
            ) : defaultListQuery.isError ? (
              <div className="mt-2 text-[12px] text-red-600">{t('Schedule.Configuration.TaskTypes.LoadError')}</div>
            ) : scheduleTaskTypes.length === 0 ? (
              <div className="mt-2 text-[12px] text-gray-500">{t('Schedule.Configuration.TaskTypes.Empty')}</div>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {scheduleTaskTypes.map((taskType) => (
                  <div key={taskType.id} className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-[12px] font-medium text-brand-700">
                      {taskType.name}
                    </span>
                    {taskType.categories.map((category) => (
                      <span
                        key={category}
                        className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-600">
                        {category}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-gray-200 bg-white px-7 py-3">
        {canManage && schedule.status !== 'ended' && (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={isUpdatingStatus}
              onClick={handleToggleStatus}
              className="h-8 flex-shrink-0 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
              {isPaused ? <Play size={16} /> : <Pause size={16} />}
              {t(isPaused ? 'Schedule.Configuration.Resume' : 'Schedule.Configuration.Pause')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isUpdatingStatus}
              onClick={() => setIsEndOpen(true)}
              className="h-8 flex-shrink-0 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
              <CircleStop size={16} />
              {t('Schedule.Configuration.End')}
            </Button>
          </>
        )}
        <span className="flex-1" />
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsDeleteOpen(true)}
            className="h-8 text-gray-500 hover:text-red-600">
            <Trash2 size={16} />
            {t('Schedule.Delete.Action')}
          </Button>
        )}
      </div>

      {isStoresOpen && (
        <Modal
          modalOpen
          closeModal={() => setIsStoresOpen(false)}
          className="max-w-lg items-stretch justify-start gap-4 p-0">
          <div className="flex cursor-default items-center gap-3 border-b border-gray-100 px-5 py-4 pr-12">
            {retailer !== null && (
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: getRetailerColor(retailer) }}>
                {getRetailerShort(retailer)}
              </span>
            )}
            <div className="min-w-0">
              <div className="truncate text-base font-semibold text-gray-900">
                {t('Schedule.Configuration.Stores.ModalTitle', {
                  retailer: retailer === null ? '' : getRetailerName(retailer),
                })}
              </div>
              <div className="text-xs text-gray-500">{t('Schedule.Configuration.Stores.ModalSubtitle')}</div>
            </div>
          </div>

          <div className="flex cursor-default flex-col gap-3">
            <div className="px-5">
              <Input
                type="text"
                aria-label={t('Schedule.Configuration.Stores.SearchPlaceholder')}
                placeholder={t('Schedule.Configuration.Stores.SearchPlaceholder')}
                value={storeSearch}
                onChange={(event) => setStoreSearch(event.target.value)}
                className="w-full cursor-text"
              />
            </div>

            <div className="max-h-72 w-full overflow-y-auto">
              {isStoresPending && <Spinner className="my-8" />}

              {!isStoresPending && isStoresError && (
                <p className="py-6 text-center text-sm text-red-500">{t('Schedule.Configuration.Stores.LoadError')}</p>
              )}

              {!isStoresPending && !isStoresError && visibleStoreRows.length === 0 && (
                <Empty className="border-0 p-6">
                  <EmptyHeader>
                    <EmptyTitle className="text-sm font-normal text-gray-500">
                      {t('Schedule.Configuration.Stores.Empty')}
                    </EmptyTitle>
                  </EmptyHeader>
                </Empty>
              )}

              {!isStoresPending &&
                !isStoresError &&
                visibleStoreRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex w-full items-center gap-2.5 border-b border-gray-100 px-5 py-2.5 text-left text-sm text-gray-700 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{row.name ?? row.locationCode}</div>
                      <div className="text-xs text-gray-400">{row.locationCode}</div>
                    </div>
                    {!row.isActive && (
                      <Badge
                        variant="gray"
                        label={t('Schedule.Configuration.Stores.Inactive')}
                        className="rounded-full"
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="flex cursor-default items-center justify-between border-t border-gray-100 px-5 py-3">
            <span className="text-xs text-gray-500">
              {t('Schedule.Configuration.Stores.Footer', {
                shown: String(visibleStoreRows.length),
                total: String(storeRows.length),
              })}
            </span>
            <Button size="sm" onClick={() => setIsStoresOpen(false)} className="h-8">
              {t('Schedule.Configuration.Stores.Done')}
            </Button>
          </div>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={pendingCeiling !== null}
        onClose={() => setPendingCeiling(null)}
        onConfirm={() => {
          const value = pendingCeiling;
          setPendingCeiling(null);
          if (value !== null) saveCeiling(value).catch(() => {});
        }}
        confirmText={t('Schedule.Configuration.Ceiling.SwitchConfirm')}
        buttonNamePrefix="SCHEDULE_CEILING_SWITCH"
      />

      <ConfirmationModal
        isOpen={isEndOpen}
        onClose={() => setIsEndOpen(false)}
        onConfirm={() => {
          handleEnd().catch(() => {});
        }}
        confirmText={t('Schedule.Configuration.EndConfirm', { name: schedule.name })}
        buttonNamePrefix="SCHEDULE_END"
      />

      <DeleteScheduleDialog
        schedule={isDeleteOpen ? schedule : null}
        onClose={() => setIsDeleteOpen(false)}
        onDeleted={() => navigate('/schedules')}
      />
    </div>
  );
};

export default ScheduleConfiguration;
